import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Link, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useAuthStore } from "@/stores/auth-store"
import { ApiClientError } from "@/lib/api-client"
import { Button } from "@/components/ui/button"

const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
  remember_me: z.boolean().optional(),
})

type LoginForm = z.infer<typeof loginSchema>

export function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { login } = useAuthStore()
  const [error, setError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginForm) => {
    try {
      setError(null)
      const result = await login(data.email, data.password, data.remember_me)
      if (result.mfa_required) {
        navigate("/mfa-verify", { state: { userId: result.user_id } })
      } else {
        navigate("/dashboard")
      }
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message)
      } else {
        setError("Wystąpił nieoczekiwany błąd")
      }
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <h2 className="page-title">
        {t("auth.login")}
      </h2>

      {error && (
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-muted-foreground">
          {t("auth.email")}
        </label>
        <input
          type="email"
          autoComplete="email"
          {...register("email")}
          className={`h-9 w-full rounded-md border bg-transparent px-3 text-sm text-foreground outline-none ${errors.email ? "border-red-500" : "border-input"}`}
        />
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-muted-foreground">
          {t("auth.password")}
        </label>
        <input
          type="password"
          autoComplete="current-password"
          {...register("password")}
          className={`h-9 w-full rounded-md border bg-transparent px-3 text-sm text-foreground outline-none ${errors.password ? "border-red-500" : "border-input"}`}
        />
      </div>

      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input type="checkbox" {...register("remember_me")} className="rounded" />
          {t("auth.rememberMe")}
        </label>
        <Link
          to="/forgot-password"
          className="text-sm text-primary hover:underline"
        >
          {t("auth.forgotPassword")}
        </Link>
      </div>

      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? t("common.loading") : t("auth.login")}
      </Button>

      <p className="text-center text-sm text-muted-foreground">
        {t("auth.noAccount")}{" "}
        <Link to="/register" className="text-primary hover:underline">
          {t("auth.register")}
        </Link>
      </p>
    </form>
  )
}
